import { z } from 'zod'

import {
  bpsSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  noteSchema,
  nonNegativePaisaSchema,
  paymentMethodSchema,
  positivePaisaSchema,
  ratioSchema,
  versionSchema,
} from '../common'

/**
 * A23–A25 · Selling, and being paid for it.
 */

/* ── A23 · POST /animals/:id/sale ─────────────────────────────────────────── */

/**
 * Confirm the sale. The client sends what happened — the price, who bought,
 * what the dalal took — and **nothing it computed**. There is no `profitPaisa`
 * on this request and there never will be.
 *
 * `commissionBps` is optional: omitted, the tenant's current default is used
 * and **snapshotted onto the sale row**, so changing the default next season
 * cannot retro-alter a booked deal.
 *
 * Permitted for a money role, or for Staff when `staff_can_record_sale` is on
 * (C12) — in which case the sale is flagged `needsReview` and the Staff member
 * still never sees the profit it produced.
 */
export const createSaleRequest = z.object({
  saleDate: isoDateSchema,
  salePricePaisa: positivePaisaSchema,
  buyerPartyId: idSchema.optional(),
  buyerName: z.string().trim().max(120).optional(),
  saleMandi: z.string().trim().max(120).optional(),
  dalalPartyId: idSchema.optional(),
  commissionBps: bpsSchema.optional(),
  /** Sale-side costs beyond commission. `side = SALE`: deducted, never added. */
  transportPaisa: nonNegativePaisaSchema.optional(),
  mandiFeePaisa: nonNegativePaisaSchema.optional(),
  otherSaleCostsPaisa: nonNegativePaisaSchema.optional(),
  /** Cash taken on the day, recorded as the first payment in the same tx. */
  paymentReceivedPaisa: nonNegativePaisaSchema.optional(),
  paymentMethod: paymentMethodSchema.optional(),
  notes: noteSchema.optional(),
})

/**
 * §4.10 · The authoritative outcome, recomputed server-side from scratch. Every
 * figure the app shows after a sale comes from here — never from the live
 * preview the slider was drawing a moment earlier.
 *
 * `profitPerDayPaisa` is the number that ranks a trade: Rs 32,703 over 90 days
 * is a worse deal than Rs 25,000 over 30, and the headline profit hides it.
 */
export const saleOutcomeSchema = z.object({
  id: idSchema,
  animalId: idSchema,
  saleDate: isoDateSchema,
  daysHeld: z.number().int().nonnegative(),
  buyerPartyId: idSchema.nullish(),
  buyerName: z.string().nullish(),
  saleMandi: z.string().nullish(),
  dalalPartyId: idSchema.nullish(),
  /** Snapshotted at sale time; the tenant default may have moved since. */
  commissionBps: bpsSchema,
  salePricePaisa: moneyPaisa(),
  commissionPaisa: moneyPaisa(),
  saleSideCostsPaisa: moneyPaisa(),
  netProceedsPaisa: moneyPaisa(),
  costBasisPaisa: moneyPaisa(),
  profitPaisa: moneyPaisa(),
  profitPerDayPaisa: moneyPaisa(),
  roiPercent: ratioSchema.optional(),
  annualisedRoiPercent: ratioSchema.optional(),
  paidPaisa: moneyPaisa(),
  outstandingPaisa: moneyPaisa(),
  /** True when Staff recorded it under C12 — the owner is asked to confirm. */
  needsReview: z.boolean(),
  notes: noteSchema.nullish(),
  version: versionSchema,
  createdAt: isoInstantSchema,
})

/* ── A24 · GET /sales/:id · PATCH · DELETE ────────────────────────────────── */

/**
 * Correcting a sale is an owner action and is audited with before/after, because
 * it moves a booked profit. Amending the price re-derives commission and every
 * figure downstream.
 */
export const patchSaleRequest = createSaleRequest
  .omit({ paymentReceivedPaisa: true, paymentMethod: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

/* ── A25 · POST /sales/:id/payments · GET ─────────────────────────────────── */

/**
 * A payment against a sale. The sum of payments may not exceed the sale price —
 * an over-payment is a typo, and a typo that silently becomes a negative
 * receivable is worse than a `409 CONFLICT`.
 */
export const createPaymentRequest = z.object({
  paidOn: isoDateSchema,
  amountPaisa: positivePaisaSchema,
  method: paymentMethodSchema,
  reference: z.string().trim().max(120).optional(),
  note: noteSchema.optional(),
})

export const paymentSchema = z.object({
  id: idSchema,
  saleId: idSchema,
  paidOn: isoDateSchema,
  amountPaisa: moneyPaisa(),
  method: paymentMethodSchema,
  reference: z.string().nullish(),
  note: noteSchema.nullish(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

export const paymentCreatedResponse = z.object({
  payment: paymentSchema,
  /** Recomputed after the write, so the screen never adds up its own balance. */
  paidPaisa: moneyPaisa(),
  outstandingPaisa: moneyPaisa(),
})

export const paymentListResponse = z.array(paymentSchema)

export type CreateSaleRequest = z.input<typeof createSaleRequest>
export type PatchSaleRequest = z.input<typeof patchSaleRequest>
export type SaleOutcome = z.infer<typeof saleOutcomeSchema>
export type CreatePaymentRequest = z.input<typeof createPaymentRequest>
export type Payment = z.infer<typeof paymentSchema>
export type PaymentCreatedResponse = z.infer<typeof paymentCreatedResponse>
